import { type ReactNode, useRef, useState } from "react";
import { Loader2, ArrowDown } from "lucide-react";
import { haptic } from "@/lib/haptics";
import { cn } from "@/lib/utils";

interface PullToRefreshProps {
  /** Called when the user pulls past the threshold. Await it to keep the spinner up. */
  onRefresh: () => Promise<void> | void;
  children: ReactNode;
  className?: string;
}

const THRESHOLD = 70; // px of pull needed to trigger
const MAX_PULL = 110; // px cap for the elastic zone

/**
 * Scroll container with a native-feeling pull-to-refresh gesture. Only engages
 * when the content is scrolled to the very top. Used as the mobile scroll area.
 */
export function PullToRefresh({ onRefresh, children, className }: PullToRefreshProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const startY = useRef<number | null>(null);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const onTouchStart = (e: React.TouchEvent) => {
    if (refreshing) return;
    startY.current = (scrollRef.current?.scrollTop ?? 0) <= 0 ? e.touches[0].clientY : null;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (startY.current === null || refreshing) return;
    // Bail if the user has scrolled down in the meantime.
    if ((scrollRef.current?.scrollTop ?? 0) > 0) { startY.current = null; setPull(0); return; }
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0) setPull(Math.min(MAX_PULL, dy * 0.5)); // elastic resistance
  };

  const onTouchEnd = async () => {
    if (startY.current === null) return;
    startY.current = null;
    if (pull >= THRESHOLD && !refreshing) {
      haptic();
      setRefreshing(true);
      setPull(THRESHOLD);
      try {
        await onRefresh();
      } finally {
        setRefreshing(false);
        setPull(0);
      }
    } else {
      setPull(0);
    }
  };

  const progress = Math.min(1, pull / THRESHOLD);

  return (
    <div
      ref={scrollRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      className={cn("relative overflow-y-auto overscroll-contain", className)}
    >
      {/* Pull indicator — height grows with the drag, no layout jump on release. */}
      <div
        className="flex items-end justify-center overflow-hidden"
        style={{ height: pull, transition: startY.current === null ? "height 0.25s ease" : "none" }}
      >
        <div className="pb-2 text-primary" style={{ opacity: progress }}>
          {refreshing ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <ArrowDown
              className="h-5 w-5 transition-transform"
              style={{ transform: `rotate(${progress >= 1 ? 180 : 0}deg)` }}
            />
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
